import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { FaPlusCircle, FaTrash, FaEraser } from 'react-icons/fa';
import { timesheetuserService } from '../../services/timesheetUserservice';

// Column order below mirrors the "Multi User Upload" table opened by
// setmultiuploaddialog() in bulkusersuploadnew.js (same fields, same order,
// same header text as the downloadable Excel Template on that page), so
// values copied from that dialog or from the downloaded template can be
// pasted straight into this grid.
const COLUMN_COUNT = 14;
const ISADDER_COL_INDEX = 13;

const makeEmptyRow = () => {
  const row = new Array(COLUMN_COUNT).fill('');
  row[ISADDER_COL_INDEX] = 'Disabled'; // matches emptyMultiUserRow.isadder default ('0')
  return row;
};

// The API reports failures as e.g. { "data": "User Already There" } (status
// 201/400/...), so surface that text; fall back for other shapes.
const getApiMessage = (response) => {
  const payload = response && response.data;
  if (payload === undefined || payload === null) {
    return 'No response from server';
  }
  const msg = payload.data !== undefined ? payload.data : (payload.message !== undefined ? payload.message : payload);
  return typeof msg === 'string' ? msg : JSON.stringify(msg);
};

// The grid shows/accepts dates as DD-MM-YYYY, but the API stores whatever
// string is sent straight into a DATE column with no reformatting (see
// UserController::signupusr), so a valid DD-MM-YYYY cell is converted to
// YYYY-MM-DD before it goes into the submitted row.
const parseDMYDate = (value) => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12) return null;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;
  return { day, month, year };
};

const isValidDmyDate = (value) => parseDMYDate(value) !== null;

const toIsoDate = (value) => {
  const parsed = parseDMYDate(value);
  if (!parsed) return value;
  const pad = (n) => String(n).padStart(2, '0');
  return `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}`;
};

const Mulituploadv2 = ({ tabname }) => {

  const toast = useRef(null);
  const cellRefs = useRef({});
  const dropdownPopupRef = useRef(null);
  const dropdownSearchRef = useRef(null);
  const dropdownOptionRefs = useRef([]);

  const userdata = localStorage.getItem('user') != '' ? JSON.parse(localStorage.getItem('user')) : {};

  const [gridRows, setGridRows] = useState(() => [makeEmptyRow()]);
  const [openCell, setOpenCell] = useState(null); // { rIdx, cIdx } of the currently open dropdown, or null
  const [dropdownRect, setDropdownRect] = useState(null); // viewport position for the open dropdown's portal
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [editingCell, setEditingCell] = useState(null); // { rIdx, cIdx } of the text cell being edited in place, or null

  const [deptnames, setdeptnames] = useState([]);
  const [teamnames, setteamnames] = useState([]);
  const [rolenames, setrolenames] = useState([]);
  const [projectnames, setprojectnames] = useState([]);
  const [approverList, setApproverList] = useState([]); // [{id, name}]
  const [costcenterList, setCostcenterList] = useState([]); // [{id, name}]

  useEffect(() => {
    getdependencies();
  }, []);

  function getdependencies() {
    try {
      timesheetuserService.getdept().then((response) => {
        if (response != undefined && response.data != undefined) {
          setdeptnames(response.data.data.map((value) => value.dept_name));
        }
      });

      timesheetuserService.getteam().then((response) => {
        if (response != undefined && response.data != undefined) {
          setteamnames(response.data.data.map((value) => value.team_name));
        }
      });

      timesheetuserService.getrole().then((response) => {
        if (response != undefined && response.data != undefined) {
          const rolenamestemp = [];
          response.data.data.forEach((value) => {
            if (userdata.id != 1) {
              if (value.rolename != 'admin') {
                rolenamestemp.push(value.rolename);
              }
            } else {
              rolenamestemp.push(value.rolename);
            }
          });
          setrolenames(rolenamestemp);
        }
      });

      timesheetuserService.getproject().then((response) => {
        if (response != undefined && response.data != undefined) {
          setprojectnames(response.data.data.map((value) => value.name));
        }
      });

      timesheetuserService.getApprover().then((response) => {
        if (response != undefined && response.data != undefined) {
          setApproverList(response.data.data.map((value) => ({ id: value.id, name: value.name })));
        }
      });

      timesheetuserService.getcostcenter().then((response) => {
        if (response != undefined && response.data != undefined) {
          setCostcenterList(response.data.data.map((value) => ({ id: value.id, name: value.costcentername })));
        }
      });
    } catch (error) {
      console.log(error);
    }
  }

  // Same 14 fields/order as emptyMultiUserRow in bulkusersuploadnew.js.
  // `width` drives the actual column sizing (see the table's style below) -
  // with table-layout:fixed, only the "width" property sizes columns; a
  // plain minWidth is not part of that layout algorithm and gets ignored,
  // which is why the columns were rendering compressed/compact before.
  const columns = useMemo(() => ([
    { key: 'userid', label: 'Email address', required: true, type: 'text', width: 240 },
    { key: 'name', label: 'Name', required: true, type: 'text', width: 170 },
    { key: 'date_of_joining', label: 'Date of Joining (DD-MM-YYYY)', required: false, type: 'date', width: 190 },
    { key: 'date_of_handovers', label: 'Date of Handover to Operations (DD-MM-YYYY)', required: false, type: 'date', width: 220 },
    { key: 'project_name', label: 'Name of the Project', required: true, type: 'text', width: 200 },
    { key: 'departmentname', label: 'Department Name', required: true, type: 'list', options: deptnames, width: 190 },
    { key: 'teamname', label: 'Team Name', required: true, type: 'list', options: teamnames, width: 170 },
    { key: 'roletype', label: 'Role', required: true, type: 'list', options: rolenames, width: 170 },
    { key: 'role', label: 'User Type', required: true, type: 'map', options: ['Manager', 'Normal User'], values: ['UL1', 'CL1'], width: 160 },
    { key: 'project', label: 'project', required: true, type: 'list', options: projectnames, width: 170 },
    { key: 'approver', label: 'Aprover', required: true, type: 'map', options: approverList.map((a) => a.name), values: approverList.map((a) => String(a.id)), width: 190 },
    { key: 'costcenter', label: 'Cost Center', required: true, type: 'map', options: costcenterList.map((c) => c.name), values: costcenterList.map((c) => String(c.id)), width: 190 },
    { key: 'employeeid', label: 'Employee ID', required: true, type: 'text', width: 140 },
    { key: 'isadder', label: 'Add Permission', required: true, type: 'map', options: ['Enabled', 'Disabled'], values: ['1', '0'], width: 160 },
  ]), [deptnames, teamnames, rolenames, projectnames, approverList, costcenterList]);

  const resolveCell = (col, raw) => {
    const trimmed = (raw ?? '').toString().trim();
    if (trimmed === '') {
      return { stored: '', valid: true };
    }
    switch (col.type) {
      case 'date': {
        const valid = isValidDmyDate(trimmed);
        return { stored: valid ? toIsoDate(trimmed) : trimmed, valid };
      }
      case 'list': {
        const match = (col.options || []).find((opt) => opt.toLowerCase() === trimmed.toLowerCase());
        return { stored: match || trimmed, valid: !!match };
      }
      case 'map': {
        const idx = (col.options || []).findIndex((opt) => opt.toLowerCase() === trimmed.toLowerCase());
        return { stored: idx >= 0 ? col.values[idx] : '', valid: idx >= 0 };
      }
      default:
        return { stored: trimmed, valid: true };
    }
  };

  const isCellValid = (rIdx, cIdx) => resolveCell(columns[cIdx], gridRows[rIdx][cIdx]).valid;

  const isRowComplete = (rIdx) => columns.every((col, cIdx) => {
    const { stored, valid } = resolveCell(col, gridRows[rIdx][cIdx]);
    if (!valid) return false;
    if (col.required && stored === '') return false;
    return true;
  });

  const addGridRow = () => setGridRows((prev) => [...prev, makeEmptyRow()]);

  const removeGridRow = (rIdx) => setGridRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== rIdx) : prev));

  const clearGrid = () => setGridRows([makeEmptyRow()]);

  const handleCellChange = (rIdx, cIdx, value) => {
    setGridRows((prev) => {
      const updated = prev.map((row) => [...row]);
      updated[rIdx][cIdx] = value;
      return updated;
    });
  };

  // Excel-style multi-cell paste: tab-separated columns, newline-separated rows.
  const handlePaste = (e, rIdx, cIdx) => {
    const text = e.clipboardData.getData('text');
    if (text.indexOf('\t') === -1 && text.indexOf('\n') === -1) {
      return; // single value, let the browser paste it into the cell normally
    }
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n');
    while (lines.length > 0 && lines[lines.length - 1] === '') {
      lines.pop();
    }
    setGridRows((prev) => {
      const updated = prev.map((row) => [...row]);
      lines.forEach((line, rOffset) => {
        const cells = line.split('\t');
        const targetRow = rIdx + rOffset;
        while (updated.length <= targetRow) {
          updated.push(makeEmptyRow());
        }
        cells.forEach((val, cOffset) => {
          const targetCol = cIdx + cOffset;
          if (targetCol < COLUMN_COUNT) {
            updated[targetRow][targetCol] = val.trim();
          }
        });
      });
      return updated;
    });
  };

  const setCellRef = (rIdx, cIdx, el) => {
    const key = `${rIdx}_${cIdx}`;
    if (el) {
      cellRefs.current[key] = el;
    } else {
      delete cellRefs.current[key];
    }
  };

  const focusCell = (rIdx, cIdx) => {
    if (rIdx < 0 || cIdx < 0 || cIdx >= COLUMN_COUNT || rIdx >= gridRows.length) {
      return;
    }
    const el = cellRefs.current[`${rIdx}_${cIdx}`];
    if (el) {
      el.focus();
      if (typeof el.select === 'function') {
        el.select(); // text inputs only - dropdown cells are plain divs
      }
    }
  };

  // --- Custom dropdown cells (Department, Team, Role, User Type, project,
  // Aprover, Cost Center, Add Permission) ---------------------------------
  // A native <select> steals ArrowUp/ArrowDown to change its own value, which
  // would break grid navigation, so these columns render as a focusable div
  // with a click-to-open, searchable option list instead. Closed, they
  // behave exactly like the text cells for navigation/paste; open, a search
  // box (auto-focused) filters the list, arrows move the highlighted option,
  // and Enter confirms it.
  const isDropdownOpen = (rIdx, cIdx) => !!openCell && openCell.rIdx === rIdx && openCell.cIdx === cIdx;

  const getFilteredOptions = (col) => {
    const base = col.options || [];
    const term = dropdownSearch.trim().toLowerCase();
    const matches = term === '' ? base : base.filter((opt) => opt.toLowerCase().includes(term));
    return col.key === 'isadder' ? matches : ['', ...matches];
  };

  // The table scrolls horizontally (overflow-x: auto), and any scrollable
  // ancestor clips a position:absolute popup regardless of z-index - so the
  // option list is portaled to document.body and positioned in viewport
  // coordinates instead of being laid out inside the table.
  const openDropdownAt = (rIdx, cIdx) => {
    const el = cellRefs.current[`${rIdx}_${cIdx}`];
    if (el) {
      const rect = el.getBoundingClientRect();
      const popupMaxWidth = 320;
      const popupMaxHeight = 260;

      let left = rect.left;
      if (left + popupMaxWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popupMaxWidth - 8);
      }

      let top = rect.bottom;
      if (top + popupMaxHeight > window.innerHeight) {
        top = Math.max(8, rect.top - popupMaxHeight);
      }

      setDropdownRect({ top, left, width: rect.width });
    }
    setOpenCell({ rIdx, cIdx });
    setDropdownSearch('');
    setHighlightIndex(0);
  };

  const closeDropdown = () => {
    setOpenCell(null);
    setDropdownRect(null);
  };

  // Auto-focus the search box whenever a dropdown opens.
  useEffect(() => {
    if (openCell && dropdownSearchRef.current) {
      dropdownSearchRef.current.focus();
    }
  }, [openCell]);

  // Keep the highlighted option scrolled into view as the user moves it
  // with the arrow keys - otherwise the highlight can move past the edge of
  // the visible list while the scrollbar stays put.
  useEffect(() => {
    if (!openCell) {
      return;
    }
    const el = dropdownOptionRefs.current[highlightIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [openCell, highlightIndex]);

  // Close on any mousedown outside the open cell's wrapper and its portaled
  // popup (they live in different DOM subtrees once the popup is portaled to
  // document.body, so a plain onBlur on the wrapper can't tell them apart).
  useEffect(() => {
    if (!openCell) {
      return undefined;
    }
    const handleDocMouseDown = (e) => {
      const popupEl = dropdownPopupRef.current;
      const wrapperEl = cellRefs.current[`${openCell.rIdx}_${openCell.cIdx}`];
      if ((popupEl && popupEl.contains(e.target)) || (wrapperEl && wrapperEl.contains(e.target))) {
        return;
      }
      closeDropdown();
    };
    document.addEventListener('mousedown', handleDocMouseDown, true);
    return () => document.removeEventListener('mousedown', handleDocMouseDown, true);
  }, [openCell]);

  // Close instead of drifting out of alignment if the page (or the table's
  // own horizontal scroller) scrolls, or the window resizes, while open -
  // but not when the scroll is just the user scrolling through the open
  // option list itself (that scroll event is caught here too, since scroll
  // events don't bubble but a capturing window listener still sees them).
  useEffect(() => {
    if (!openCell) {
      return undefined;
    }
    const handleScroll = (e) => {
      if (dropdownPopupRef.current && dropdownPopupRef.current.contains(e.target)) {
        return;
      }
      closeDropdown();
    };
    const handleResize = () => closeDropdown();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [openCell]);

  const selectOption = (rIdx, cIdx, optionValue) => {
    handleCellChange(rIdx, cIdx, optionValue);
    closeDropdown();
    focusCell(rIdx, cIdx); // hand keyboard focus back to the cell for continued grid navigation
  };

  // Closed-cell keydown: Enter / Space / F2 / Alt+ArrowDown opens the list
  // (this handler is only used by dropdown cells, so Enter here means "open"
  // rather than "move down a row" as it does on the plain text cells);
  // everything else (arrows, Tab) behaves exactly like a text cell.
  const handleClosedCellKeyDown = (e, rIdx, cIdx) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'F2' || (e.key === 'ArrowDown' && e.altKey)) {
      e.preventDefault();
      openDropdownAt(rIdx, cIdx);
      return;
    }
    handleKeyDown(e, rIdx, cIdx);
  };

  // Keydown while the search box (inside the open popup) has focus.
  const handleDropdownSearchKeyDown = (e, rIdx, cIdx, filteredOptions) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions.length > 0) {
          selectOption(rIdx, cIdx, filteredOptions[Math.min(highlightIndex, filteredOptions.length - 1)]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        focusCell(rIdx, cIdx);
        break;
      case 'Tab':
        e.preventDefault();
        closeDropdown();
        focusCell(rIdx, e.shiftKey ? cIdx - 1 : cIdx + 1);
        break;
      default:
        break;
    }
  };

  const isEditingCell = (rIdx, cIdx) => !!editingCell && editingCell.rIdx === rIdx && editingCell.cIdx === cIdx;

  // Arrow keys move between cells (Excel "navigation mode") - except once a
  // text cell has been double-clicked (or F2'd) into "edit mode", where the
  // arrows are left alone so they move the text cursor inside the cell
  // instead of jumping to a neighbour.
  const handleKeyDown = (e, rIdx, cIdx) => {
    if (isEditingCell(rIdx, cIdx)) {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          setEditingCell(null);
          focusCell(e.shiftKey ? rIdx - 1 : rIdx + 1, cIdx);
          return;
        case 'Escape':
          e.preventDefault();
          setEditingCell(null);
          return;
        case 'Tab':
          e.preventDefault();
          setEditingCell(null);
          focusCell(rIdx, e.shiftKey ? cIdx - 1 : cIdx + 1);
          return;
        default:
          return; // ArrowUp/Down/Left/Right etc. - let the input move its own cursor/selection
      }
    }

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        focusCell(rIdx - 1, cIdx);
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusCell(rIdx + 1, cIdx);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusCell(rIdx, cIdx - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        focusCell(rIdx, cIdx + 1);
        break;
      case 'Enter':
        e.preventDefault();
        focusCell(e.shiftKey ? rIdx - 1 : rIdx + 1, cIdx);
        break;
      case 'Tab':
        e.preventDefault();
        focusCell(rIdx, e.shiftKey ? cIdx - 1 : cIdx + 1);
        break;
      case 'F2':
        e.preventDefault();
        setEditingCell({ rIdx, cIdx });
        break;
      default:
        break;
    }
  };

  const buildRowsToSubmit = () => {
    const rowsToSubmit = [];
    const rowErrors = [];

    gridRows.forEach((rowCells, rIdx) => {
      const allEmpty = rowCells.every((cell) => (cell ?? '').toString().trim() === '');
      if (allEmpty) {
        return;
      }

      const rowObj = {};
      let hasInvalid = false;
      let hasMissing = false;

      columns.forEach((col, cIdx) => {
        const { stored, valid } = resolveCell(col, rowCells[cIdx]);
        rowObj[col.key] = stored;
        if (!valid) hasInvalid = true;
        if (col.required && stored === '') hasMissing = true;
      });

      if (hasInvalid || hasMissing) {
        rowErrors.push({ row: rIdx + 1, hasInvalid, hasMissing });
      } else {
        rowsToSubmit.push({ rIdx, data: rowObj });
      }
    });

    return { rowsToSubmit, rowErrors };
  };

  const submitGrid = async () => {
    const { rowsToSubmit, rowErrors } = buildRowsToSubmit();

    if (rowErrors.length > 0) {
      const detail = rowErrors.map((r) => {
        const reasons = [];
        if (r.hasMissing) reasons.push('missing required value(s)');
        if (r.hasInvalid) reasons.push('invalid dropdown/date value(s)');
        return `Row ${r.row}: ${reasons.join(' & ')}`;
      }).join(' | ');
      toast.current.show({ severity: 'error', summary: 'Multi User Upload', detail, life: 6000 });
      return;
    }

    if (rowsToSubmit.length === 0) {
      toast.current.show({ severity: 'warn', summary: 'Multi User Upload', detail: 'Add at least one user row', life: 3000 });
      return;
    }

    const succeededRows = new Set();
    const failures = []; // { row, userid, message } - message is the API's own text

    for (const { rIdx, data } of rowsToSubmit) {
      try {
        const response = await timesheetuserService.addUser({ formdata: JSON.stringify(data) });
        if (response && response.status == 200) {
          succeededRows.add(rIdx);
        } else {
          failures.push({ row: rIdx + 1, userid: data.userid, message: getApiMessage(response) });
        }
      } catch (error) {
        failures.push({ row: rIdx + 1, userid: data.userid, message: 'Request failed' });
      }
    }

    const successCount = succeededRows.size;
    const failCount = failures.length;
    const maxListed = 10;

    toast.current.show({
      severity: failCount === 0 ? 'success' : (successCount === 0 ? 'error' : 'warn'),
      summary: 'Multi User Upload',
      detail: (
        <div>
          <div>{successCount} user(s) added successfully{failCount > 0 ? `, ${failCount} failed` : ''}</div>
          {failures.slice(0, maxListed).map((f) => (
            <div key={f.row}>Row {f.row} ({f.userid}): {f.message}</div>
          ))}
          {failCount > maxListed && <div>...and {failCount - maxListed} more</div>}
        </div>
      ),
      life: failCount === 0 ? 4000 : 10000
    });

    // Drop only the rows that were saved, so any that failed stay in the
    // grid to be corrected and retried.
    if (successCount > 0) {
      setGridRows((prev) => {
        const remaining = prev.filter((_, i) => !succeededRows.has(i));
        return remaining.length > 0 ? remaining : [makeEmptyRow()];
      });
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="invisible-divider-sm"></div>

      <div className='card card-custom-style card-style-padding'>
        <h6 className='card-style-padding-two'>{tabname}</h6>

        <div className='row'>
          <div className='col-md-12'>
            <Message
              severity="info"
              className='custom-width-style'
              style={{ width: '100%', justifyContent: 'flex-start' }}
              text="Click a text cell and type, or paste a block of cells copied from Excel (or from the Multi User Upload dialog) - it will fill across rows and columns just like a spreadsheet. Use the arrow keys to move between cells. Department, Team, Role, User Type, project, Aprover, Cost Center and Add Permission are dropdowns - click one (or press Enter/Space/F2 while it's focused) to open it, type to search/filter the list, use the arrow keys to pick a value and Enter to confirm."
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-md-12'>
            <Message
              severity="error"
              className='custom-width-style'
              style={{ width: '100%', justifyContent: 'flex-start' }}
              text="Enter Date of Joining / Date of Handover as text in DD-MM-YYYY format (e.g. 25-08-2026). Cells with a red border have a value that does not match an allowed option or date format - fix them before saving."
            />
          </div>
        </div>

        <br />

        {/* The app layout puts pages inside a flex item (.content-box, flex:0.8)
            whose automatic min-width is its content's min-content - so a wide
            table here would stretch that whole column and scroll the page
            body sideways. width:0 + minWidth:'100%' makes this scroller
            contribute nothing to that intrinsic sizing while still filling
            the available width, so the overflow is scrolled inside this div. */}
        <div style={{ overflowX: 'auto', width: 0, minWidth: '100%' }}>
          {/* table-layout:fixed sizes columns from each th's "width" (min-width
              isn't part of that algorithm); width:'max-content' + minWidth:'100%'
              stops Bootstrap's .table 100%-width rule from squeezing all columns
              down to an equal, cramped share of the container. */}
          <table className="table table-bordered" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '50px' }}>Sr No</th>
                {columns.map((col) => (
                  <th key={col.key} style={{ width: `${col.width}px` }}>
                    {col.label} {col.required && <span className='error'>*</span>}
                  </th>
                ))}
                <th style={{ width: '60px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {gridRows.map((rowCells, rIdx) => (
                <tr key={rIdx} style={isRowComplete(rIdx) ? { backgroundColor: '#d4f8d4' } : undefined}>
                  <td className='text-center'>{rIdx + 1}</td>
                  {columns.map((col, cIdx) => {
                    const valid = isCellValid(rIdx, cIdx);
                    const hasDropdown = col.type === 'list' || col.type === 'map';
                    const invalidStyle = !valid ? { borderColor: '#dc3545', backgroundColor: '#fdecea' } : undefined;
                    const invalidTitle = !valid ? `"${rowCells[cIdx]}" does not match an allowed value for ${col.label}` : undefined;

                    if (hasDropdown) {
                      const open = isDropdownOpen(rIdx, cIdx);
                      const filteredOptions = open ? getFilteredOptions(col) : [];
                      if (open) {
                        dropdownOptionRefs.current = new Array(filteredOptions.length);
                      }
                      return (
                        <td key={col.key}>
                          <div
                            tabIndex={0}
                            role="button"
                            aria-haspopup="listbox"
                            className='form-control'
                            style={{
                              cursor: 'pointer',
                              userSelect: 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              ...invalidStyle
                            }}
                            ref={(el) => setCellRef(rIdx, cIdx, el)}
                            onClick={() => (open ? closeDropdown() : openDropdownAt(rIdx, cIdx))}
                            onKeyDown={(e) => handleClosedCellKeyDown(e, rIdx, cIdx)}
                            onPaste={(e) => handlePaste(e, rIdx, cIdx)}
                            title={invalidTitle}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {rowCells[cIdx] || <span style={{ color: '#999' }}>Select</span>}
                            </span>
                            <span style={{ marginLeft: '6px', fontSize: '11px' }}>▾</span>
                          </div>

                          {open && dropdownRect && createPortal(
                            <div
                              ref={dropdownPopupRef}
                              style={{
                                position: 'fixed', top: dropdownRect.top, left: dropdownRect.left,
                                zIndex: 2000, background: '#fff', border: '1px solid #ced4da', borderRadius: '4px',
                                minWidth: dropdownRect.width, maxWidth: '320px', width: 'max-content',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.2)', overflow: 'hidden'
                              }}
                            >
                              <input
                                type='text'
                                ref={dropdownSearchRef}
                                value={dropdownSearch}
                                placeholder='Search...'
                                onChange={(e) => { setDropdownSearch(e.target.value); setHighlightIndex(0); }}
                                onKeyDown={(e) => handleDropdownSearchKeyDown(e, rIdx, cIdx, filteredOptions)}
                                style={{
                                  width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                                  border: 'none', borderBottom: '1px solid #e9ecef', outline: 'none', fontSize: '13px'
                                }}
                              />
                              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {filteredOptions.length === 0 ? (
                                  <div style={{ padding: '8px 10px', color: '#999', fontStyle: 'italic' }}>No matches</div>
                                ) : filteredOptions.map((opt, oIdx) => (
                                  <div
                                    key={opt === '' ? '__blank__' : opt}
                                    ref={(el) => { dropdownOptionRefs.current[oIdx] = el; }}
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); selectOption(rIdx, cIdx, opt); }}
                                    onMouseEnter={() => setHighlightIndex(oIdx)}
                                    style={{
                                      padding: '6px 10px', whiteSpace: 'nowrap',
                                      color: opt === '' ? '#999' : 'inherit',
                                      backgroundColor: oIdx === highlightIndex ? '#e9ecef' : '#fff'
                                    }}
                                  >
                                    {opt === '' ? 'Select' : opt}
                                  </div>
                                ))}
                              </div>
                            </div>,
                            document.body
                          )}
                        </td>
                      );
                    }

                    const editing = isEditingCell(rIdx, cIdx);
                    return (
                      <td key={col.key}>
                        <input
                          type='text'
                          className='form-control'
                          style={{
                            ...(editing ? { outline: '2px solid #0d6efd', outlineOffset: '-1px' } : undefined),
                            ...invalidStyle
                          }}
                          value={rowCells[cIdx]}
                          ref={(el) => setCellRef(rIdx, cIdx, el)}
                          onChange={(e) => handleCellChange(rIdx, cIdx, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rIdx, cIdx)}
                          onPaste={(e) => handlePaste(e, rIdx, cIdx)}
                          onFocus={(e) => { if (!editing) e.target.select(); }}
                          onDoubleClick={() => setEditingCell({ rIdx, cIdx })}
                          onBlur={() => setEditingCell((prev) => (prev && prev.rIdx === rIdx && prev.cIdx === cIdx ? null : prev))}
                          title={editing ? 'Editing - arrow keys move the cursor. Press Enter/Tab/Escape to finish.' : invalidTitle}
                        />
                      </td>
                    );
                  })}
                  <td className='text-center'>
                    <FaTrash className='error custom-cursor-style' style={{ fontSize: '18px' }} onClick={() => removeGridRow(rIdx)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='container-box space-between'>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <FaPlusCircle className='info-style custom-cursor-style' style={{ fontSize: '24px' }} title="Add Row" onClick={addGridRow} />
            <FaEraser className='custom-cursor-style' style={{ fontSize: '20px' }} title="Clear Grid" onClick={clearGrid} />
          </div>
          <div className='pull-right'>
            <span>
              <Button type="button" label="Save All" icon="pi pi-check" severity="success" rounded onClick={submitGrid} />
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Mulituploadv2;
